import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Star, MapPin } from "lucide-react";
import { roomService } from "../../services/roomService";
import { contentService } from "../../services/contentService";
import { imgUrl } from "../../utils/imgUrl";
import { useViewport } from "../../hooks/useViewport";
import HowToReach from "../../components/public/HowToReach";

const FALLBACK_ROOMS = [
  {
    id: "f1",
    images: [
      "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=800&h=600&fit=crop",
    ],
    name: "Deluxe Room",
    price_per_night: 3500,
    capacity: 3,
    amenities: ["WiFi", "TV", "Geyser"],
  },
  {
    id: "f2",
    images: [
      "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&h=600&fit=crop",
    ],
    name: "Family Cottage",
    price_per_night: 5500,
    capacity: 5,
    amenities: ["WiFi", "TV", "Kitchen"],
  },
  {
    id: "f3",
    images: [
      "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&h=600&fit=crop",
    ],
    name: "Premium Cottage",
    price_per_night: 7000,
    capacity: 4,
    amenities: ["Private Balcony", "WiFi", "Mini Fridge"],
  },
];

const DEFAULTS = {
  hero: {
    bg_image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop",
    subtitle:
      // "Experience the serenity of Dhanaulti amidst the Himalayan beauty",
      "Stay close to nature",
  },
  about: {
    image:
      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&h=600&fit=crop",
    heading: "About Us",
    text1:
      "Nestled in the picturesque hill station of Dhanaulti, Burans Hikes Home Stay offers a perfect blend of comfort and natural beauty.",
    text2:
      "Whether you're seeking adventure or tranquility, our warm hospitality and stunning surroundings make for an unforgettable mountain retreat.",
  },
  stats: [
    { number: "2280m", label: "Altitude", color: "#16a34a" },
    { number: "500+", label: "Happy Guests", color: "#2563eb" },
    { number: "4.6/5", label: "Average Rating", color: "#f59e0b" },
    { number: "2+", label: "Years Experience", color: "#8b5cf6" },
  ],
  features: [
    {
      emoji: "🏔️",
      title: "Scenic Views",
      description: "Breathtaking panoramic views of the Himalayas",
    },
    {
      emoji: "🍽️",
      title: "Local Cuisine",
      description: "Authentic Garhwali delicacies prepared with love",
    },
    {
      emoji: "🥾",
      title: "Trekking Routes",
      description: "Easy access to beautiful hiking trails",
    },
    {
      emoji: "🌲",
      title: "Nature Walks",
      description: "Explore pristine deodar and oak forests",
    },
    {
      emoji: "🔥",
      title: "Bonfire Nights",
      description: "Cozy evenings under the starlit sky",
    },
    {
      emoji: "📶",
      title: "Modern Amenities",
      description: "WiFi, hot water, and comfortable rooms",
    },
  ],
  testimonials: [
    {
      name: "Rahul Sharma",
      location: "Delhi",
      rating: 5,
      text: "Amazing experience! The views were breathtaking and the hospitality was excellent. Perfect place for a peaceful getaway.",
    },
    {
      name: "Priya Patel",
      location: "Mumbai",
      rating: 5,
      text: "Loved every moment of our stay. The rooms were clean, food was delicious, and the staff was very friendly.",
    },
    {
      name: "Amit Kumar",
      location: "Bangalore",
      rating: 5,
      text: "Best homestay in Dhanaulti! Perfect for families. Kids enjoyed the bonfire and nature walks.",
    },
  ],
};

const Home = () => {
  const [rooms, setRooms] = useState(FALLBACK_ROOMS);
  const [sc, setSc] = useState(DEFAULTS);
  const [hoveredRoomId, setHoveredRoomId] = useState(null);
  const { isMobile, isDesktop } = useViewport();

  useEffect(() => {
    roomService
      .getAllRooms()
      .then((res) => {
        const r = res.data.rooms || [];
        if (r.length > 0) setRooms(r);
      })
      .catch(() => {});
    contentService
      .getSiteContent()
      .then((res) => {
        const c = res.data.content || {};
        setSc({
          hero: c.hero || DEFAULTS.hero,
          about: c.about || DEFAULTS.about,
          stats: c.stats || DEFAULTS.stats,
          features: c.features || DEFAULTS.features,
          testimonials: c.testimonials || DEFAULTS.testimonials,
        });
      })
      .catch(() => {});
  }, []);

  const statIcons = [
    <MapPin size={28} />,
    <Users size={28} />,
    <Star size={28} />,
    <Calendar size={28} />,
  ];

  // Styles
  const sectionStyle = { padding: "80px 24px" };
  const containerStyle = { maxWidth: 1200, margin: "0 auto" };
  const gridStyle = { display: "grid", gap: "24px" };
  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: 20,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    overflow: "hidden",
    transition: "transform 0.3s, box-shadow 0.3s",
  };

  const btnPrimaryStyle = {
    backgroundColor: "#16a34a",
    color: "#fff",
    padding: "12px 32px",
    borderRadius: 9999,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(22,163,74,0.4)",
    transition: "transform 0.2s, box-shadow 0.2s",
  };

  const btnOutlineStyle = {
    backgroundColor: "transparent",
    color: "#fff",
    padding: "12px 32px",
    borderRadius: 9999,
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
    border: "1.5px solid rgba(255,255,255,0.5)",
    cursor: "pointer",
    transition: "all 0.2s",
  };

  return (
    <div>
      {/* ── Hero ── */}
      <section
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `url(${sc.hero?.bg_image || DEFAULTS.hero.bg_image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(22,163,74,0.8) 0%, rgba(21,128,61,0.85) 50%, rgba(31,41,55,0.9) 100%)",
          }}
        ></div>
        <div
          style={{
            position: "relative",
            zIndex: 10,
            textAlign: "center",
            color: "#fff",
            padding: "0 24px",
            maxWidth: 900,
            margin: "0 auto",
            animation: "fadeInUp 0.8s ease-out",
          }}
        >
          <p
            style={{
              fontFamily: "'Great Vibes', cursive",
              fontSize: "clamp(40px, 6vw, 64px)",
              letterSpacing: 1,
              fontWeight: 400,
              lineHeight: 1.1,
              color: "rgba(255,255,255,0.92)",
              marginBottom: 4,
            }}
          >
            Welcome to
          </p>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(48px, 8vw, 72px)",
              fontWeight: 900,
              lineHeight: 1.1,
              marginBottom: 24,
            }}
          >
            {/* <span
              style={{
                background: "linear-gradient(135deg, #fff 0%, #e5e7eb 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Burans Hikes
              <br />
              Home Stay
            </span> */}
            <span
              style={{
                background: "linear-gradient(135deg, #fff 0%, #e5e7eb 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: "1.1",
              }}
            >
              Burans Hikes
              <br />
              <span style={{ fontSize: "0.55em", letterSpacing: "3px" }}>
                Home Stay (Resort)
              </span>
            </span>
          </h1>
          {/* <div
            style={{
              width: 96,
              height: 4,
              background: "rgba(255,255,255,0.4)",
              margin: "0 auto 32px",
              borderRadius: 2,
            }}
          ></div> */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              margin: "0 auto 32px",
            }}
          >
            <div
              style={{
                width: "70px",
                height: "2px",
                background: "rgba(255,255,255,0.5)",
              }}
            />

            <div
              style={{
                width: "10px",
                height: "10px",
                background: "#d4a017",
                transform: "rotate(45deg)",
                borderRadius: "1px",
              }}
            />

            <div
              style={{
                width: "70px",
                height: "2px",
                background: "rgba(255,255,255,0.5)",
              }}
            />
          </div>
          <p
            style={{
              fontSize: "clamp(18px, 3vw, 20px)",
              color: "rgba(255,255,255,0.85)",
              fontWeight: 300,
              lineHeight: 1.6,
              maxWidth: 700,
              margin: "0 auto 48px",
            }}
          >
            {sc.hero?.subtitle || DEFAULTS.hero.subtitle}
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 16,
              justifyContent: "center",
            }}
          >
            <Link
              to="/rooms"
              style={{ ...btnPrimaryStyle, padding: "14px 40px", fontSize: 15 }}
            >
              Explore Our Rooms
            </Link>
            <Link
              to="/about"
              style={{ ...btnOutlineStyle, padding: "14px 40px", fontSize: 15 }}
            >
              Learn More
            </Link>
          </div>
        </div>
        {/* Scroll indicator */}
        {/* <div
          style={{
            position: "absolute",
            bottom: 40,
            left: "50%",
            transform: "translateX(-50%)",
            animation: "bounce 2s ease-in-out infinite",
          }}
        >
          <div
            style={{
              width: 28,
              height: 48,
              border: "2px solid rgba(255,255,255,0.4)",
              borderRadius: 24,
              display: "flex",
              justifyContent: "center",
              paddingTop: 8,
            }}
          >
            <div
              style={{
                width: 4,
                height: 12,
                background: "rgba(255,255,255,0.6)",
                borderRadius: 2,
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            ></div>
          </div>
        </div> */}
      </section>

      {/* ── Stats ── */}
      <section style={{ ...sectionStyle, backgroundColor: "#fff" }}>
        <div style={containerStyle}>
          <div
            style={{
              ...gridStyle,
              gridTemplateColumns: isDesktop
                ? "repeat(4, 1fr)"
                : isMobile
                  ? "repeat(1, 1fr)"
                  : "repeat(2, 1fr)",
            }}
          >
            {(sc.stats || DEFAULTS.stats).map((stat, i) => (
              <div
                key={i}
                style={{
                  ...cardStyle,
                  padding: 32,
                  textAlign: "center",
                  backgroundColor: "#f9fafb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: 12,
                    color: stat.color,
                  }}
                >
                  {statIcons[i % 4]}
                </div>
                <div
                  style={{
                    fontFamily: "inherit",
                    fontSize: 40,
                    fontWeight: 400,
                    color: "#1f2937",
                    marginBottom: 4,
                  }}
                >
                  {stat.number}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontWeight: 500,
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={containerStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 64,
              alignItems: "center",
            }}
          >
            <div
              style={{
                borderRadius: 32,
                overflow: "hidden",
                boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                animation: "slideInLeft 0.8s ease-out",
              }}
            >
              <img
                src={sc.about?.image || DEFAULTS.about.image}
                alt="Mountain View"
                style={{ width: "100%", height: 480, objectFit: "cover" }}
              />
            </div>
            <div style={{ animation: "slideInRight 0.8s ease-out" }}>
              <p
                style={{
                  fontSize: 11,
                  color: "#16a34a",
                  textTransform: "uppercase",
                  letterSpacing: 4,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                Our Story
              </p>
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 48,
                  fontWeight: 900,
                  color: "#1f2937",
                  lineHeight: 1.1,
                  marginBottom: 16,
                }}
              >
                {sc.about?.heading || DEFAULTS.about.heading}
              </h2>
              <div
                style={{
                  width: 64,
                  height: 4,
                  background: "#16a34a",
                  borderRadius: 2,
                  marginBottom: 32,
                }}
              ></div>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: 17,
                  lineHeight: 1.8,
                  marginBottom: 24,
                }}
              >
                {sc.about?.text1 || DEFAULTS.about.text1}
              </p>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: 17,
                  lineHeight: 1.8,
                  marginBottom: 40,
                }}
              >
                {sc.about?.text2 || DEFAULTS.about.text2}
              </p>
              <Link
                to="/about"
                style={{
                  ...btnOutlineStyle,
                  color: "#16a34a",
                  borderColor: "#16a34a",
                  padding: "10px 28px",
                }}
              >
                Learn More About Us →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ ...sectionStyle, backgroundColor: "#fff" }}>
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <p
              style={{
                fontSize: 11,
                color: "#16a34a",
                textTransform: "uppercase",
                letterSpacing: 4,
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              Benefits
            </p>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 48,
                fontWeight: 900,
                color: "#1f2937",
              }}
            >
              Why Choose{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Us
              </span>
            </h2>
            <div
              style={{
                width: 64,
                height: 4,
                background: "#16a34a",
                borderRadius: 2,
                margin: "20px auto 0",
              }}
            ></div>
          </div>
          <div
            style={{
              ...gridStyle,
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            }}
          >
            {(sc.features || DEFAULTS.features).map((feat, i) => (
              <div
                key={i}
                style={{
                  ...cardStyle,
                  padding: 32,
                  textAlign: "center",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                }}
              >
                <div style={{ fontSize: 48, marginBottom: 20 }}>
                  {feat.emoji}
                </div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#1f2937",
                    marginBottom: 12,
                  }}
                >
                  {feat.title}
                </h3>
                <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.6 }}>
                  {feat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Rooms Preview ── */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <p
              style={{
                fontSize: 11,
                color: "#16a34a",
                textTransform: "uppercase",
                letterSpacing: 4,
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              Accommodations
            </p>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 48,
                fontWeight: 900,
                color: "#1f2937",
              }}
            >
              Our{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Rooms
              </span>
            </h2>
            <div
              style={{
                width: 64,
                height: 4,
                background: "#16a34a",
                borderRadius: 2,
                margin: "20px auto 0",
              }}
            ></div>
          </div>
          <div
            style={{
              ...gridStyle,
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            }}
          >
            {rooms.slice(0, 3).map((room) => (
              <div
                key={room.id}
                style={{ ...cardStyle, backgroundColor: "#fff", transform: hoveredRoomId === room.id ? "translateY(-10px) scale(1.02)" : "translateY(0) scale(1)", boxShadow: hoveredRoomId === room.id ? "0 20px 50px rgba(0,0,0,0.18)" : "0 4px 20px rgba(0,0,0,0.08)" }}
                onMouseEnter={() => setHoveredRoomId(room.id)}
                onMouseLeave={() => setHoveredRoomId(null)}
              >
                <figure style={{ height: 224, overflow: "hidden" }}>
                  <img
                    src={imgUrl(
                      room.images?.[0] || FALLBACK_ROOMS[0].images[0],
                    )}
                    alt={room.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </figure>
                <div style={{ padding: 24 }}>
                  <h3
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 24,
                      fontWeight: 700,
                      color: "#1f2937",
                      marginBottom: 8,
                    }}
                  >
                    {room.name}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 4,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: 32,
                        fontWeight: 900,
                        color: "#16a34a",
                      }}
                    >
                      ₹{Number(room.price_per_night).toLocaleString()}
                    </span>
                    <span style={{ fontSize: 13, color: "#9ca3af" }}>
                      /night
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 16,
                      marginBottom: 16,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        padding: "4px 10px",
                        backgroundColor: "#f0fdf4",
                        color: "#166534",
                        borderRadius: 9999,
                        border: "1px solid #86efac",
                      }}
                    >
                      Up to {room.capacity} Guests
                    </span>
                    {(room.amenities || []).slice(0, 2).map((a, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: 12,
                          padding: "4px 10px",
                          backgroundColor: "#f3f4f6",
                          color: "#374151",
                          borderRadius: 9999,
                        }}
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/rooms"
                    style={{
                      ...btnPrimaryStyle,
                      width: "100%",
                      textAlign: "center",
                      padding: "10px 0",
                    }}
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 48 }}>
            <Link
              to="/rooms"
              style={{ ...btnPrimaryStyle, padding: "16px 48px", fontSize: 16 }}
            >
              View All Rooms
            </Link>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={{ ...sectionStyle, backgroundColor: "#fff" }}>
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <p
              style={{
                fontSize: 11,
                color: "#16a34a",
                textTransform: "uppercase",
                letterSpacing: 4,
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              Reviews
            </p>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 48,
                fontWeight: 900,
                color: "#1f2937",
              }}
            >
              What Our{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Guests Say
              </span>
            </h2>
            <div
              style={{
                width: 64,
                height: 4,
                background: "#16a34a",
                borderRadius: 2,
                margin: "20px auto 0",
              }}
            ></div>
          </div>
          <div
            style={{
              ...gridStyle,
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            }}
          >
            {(sc.testimonials || DEFAULTS.testimonials).map((t, i) => (
              <div
                key={i}
                style={{
                  ...cardStyle,
                  padding: 32,
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
                  {[...Array(t.rating)].map((_, j) => (
                    <Star
                      key={j}
                      size={18}
                      style={{ fill: "#f59e0b", color: "#f59e0b" }}
                    />
                  ))}
                </div>
                <p
                  style={{
                    color: "#6b7280",
                    fontStyle: "italic",
                    lineHeight: 1.7,
                    fontSize: 14,
                    marginBottom: 20,
                  }}
                >
                  "{t.text}"
                </p>
                <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                  <p
                    style={{ fontWeight: 600, color: "#1f2937", fontSize: 14 }}
                  >
                    {t.name}
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "#9ca3af",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      marginTop: 4,
                    }}
                  >
                    <MapPin size={11} /> {t.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How to Reach ── */}
      <HowToReach />

      {/* ── CTA ── */}
      <section
        style={{
          padding: "112px 24px",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1464207687429-7505649dae38?w=1920&h=600&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(22,163,74,0.9) 0%, rgba(21,128,61,0.95) 100%)",
          }}
        ></div>
        <div
          style={{
            position: "relative",
            zIndex: 10,
            textAlign: "center",
            color: "#fff",
            maxWidth: 800,
            margin: "0 auto",
          }}
        >
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(40px, 6vw, 60px)",
              fontWeight: 900,
              lineHeight: 1.1,
              marginBottom: 24,
            }}
          >
            Ready for Your
            <br />
            Mountain Getaway?
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.8)",
              fontSize: 18,
              fontWeight: 300,
              lineHeight: 1.6,
              marginBottom: 40,
            }}
          >
            Book your stay today and create unforgettable memories in the
            Himalayas
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 16,
              justifyContent: "center",
            }}
          >
            <Link
              to="/register"
              style={{
                ...btnPrimaryStyle,
                backgroundColor: "#fff",
                color: "#16a34a",
                padding: "14px 40px",
              }}
            >
              Book Now
            </Link>
            <Link
              to="/contact"
              style={{ ...btnOutlineStyle, padding: "14px 40px" }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
